import React, { useState, useEffect } from 'react';
import { Formik, Field, Form, ErrorMessage } from 'formik';
import * as yup from 'yup';
import { Alert, Spinner, Button, Card, Row, Col, Form as BootstrapForm } from 'react-bootstrap';
// Using Bootstrap Icons (no import required)

const validationSchema = yup.object().shape({
  fullname: yup.string().required('Staff name is required'),
  phone: yup.string().required('Mobile number is required').matches(/^[0-9]{10}$/, 'Mobile number must be 10 digits'),
});

const StaffAll = () => {
  const [isLoading, setLoading] = useState(true);
  const [dataSource, setDataSource] = useState([]);
  const [staffVal, setStaffVal] = useState({
    id: null,
    fullname: '',
    phone: '',
    can_next_skip: true,
    can_print_token: true,
    can_open_token: true,
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const getAuthToken = () => {
    return localStorage.getItem("tokenly_token") || "";
  };

  useEffect(() => {
    getData();
  }, []);

  function handleResponse(flag, data, error, msg) {
    console.log('handleResponse called:', { flag, data, error, msg }); // Debug log
    if (error) {
      setError(msg);
      setTimeout(() => setError(''), 3000);
    } else {
      switch (flag) {
        case 'getData':
          console.log('Setting dataSource:', data); // Debug log
          setDataSource(data);
          setLoading(false);
          break;
        case 'editStaff':
          setStaffVal({
            id: data.id,
            fullname: data.fullname,
            phone: data.phone,
            can_next_skip: data.can_next_skip == 1 ? true : false,
            can_print_token: data.can_print_token == 1 ? true : false,
            can_open_token: data.can_open_token == 1 ? true : false,
          });
          setLoading(false);
          break;
        case 'staffAdd':
          setStaffVal({
            id: null,
            fullname: '',
            phone: '',
            can_next_skip: true,
            can_print_token: true,
            can_open_token: true,
          });
          getData();
          setSuccess(msg);
          setTimeout(() => setSuccess(''), 3000);
          break;
        case 'removeStaff':
          getData();
          setSuccess(msg);
          setTimeout(() => setSuccess(''), 3000);
          break;
      }
    }
    setLoading(false);
  }

  const getData = async () => {
    setLoading(true);
    try {
      const res = await fetch("https://tokenly.biswaas.com/api/user/staffList", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${getAuthToken()}`
        },
        body: JSON.stringify({}),
      });

      if (!res.ok) throw new Error("Failed to load staff list");

      const json = await res.json();
      console.log('API Response:', json); // Debug log
      handleResponse('getData', json.data, !json.status, json.message);
    } catch (e) {
      console.error('API Error:', e); // Debug log
      setError(e.message || "Error fetching staff list");
      setLoading(false);
    }
  };

  const editStaff = async (itemId) => {
    setLoading(true);
    try {
      const res = await fetch("https://tokenly.biswaas.com/api/doctor-detail", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${getAuthToken()}`
        },
        body: JSON.stringify({ itemId: itemId }),
      });

      if (!res.ok) throw new Error("Failed to load staff details");

      const json = await res.json();
      handleResponse('editStaff', json.data, !json.status, json.message);
    } catch (e) {
      setError(e.message || "Error fetching staff details");
      setLoading(false);
    }
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const res = await fetch("https://tokenly.biswaas.com/api/user/staffAdd", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${getAuthToken()}`
        },
        body: JSON.stringify(values),
      });

      if (!res.ok) throw new Error("Failed to add/update staff");

      const json = await res.json();
      handleResponse('staffAdd', json.data, !json.status, json.message);
    } catch (e) {
      setError(e.message || "Error adding/updating staff");
      setLoading(false);
    }
  };

  const removeStaff = async (itemId) => {
    if (!window.confirm('Are you sure you want to remove this staff member?')) {
      return;
    }
    
    setLoading(true);
    try {
      const res = await fetch("https://tokenly.biswaas.com/api/user/staffRemove", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${getAuthToken()}`
        },
        body: JSON.stringify({ itemId: itemId }),
      });

      if (!res.ok) throw new Error("Failed to remove staff");

      const json = await res.json();
      handleResponse('removeStaff', json.data, !json.status, json.message);
    } catch (e) {
      setError(e.message || "Error removing staff");
      setLoading(false);
    }
  };

  const StaffItem = ({ item }) => {
    return (
      <Card className="mb-3 shadow-sm">
        <Card.Body>
          <Row className="align-items-center">
                  <Col xs="auto">
              {item.profile_photo && item.profile_photo_full ? (
                <img
                  src={item.profile_photo_full}
                  alt={item.fullname}
                  className="rounded-circle"
                  style={{ width: '50px', height: '50px', objectFit: 'cover' }}
                />
              ) : (
                <div
                  className="rounded-circle bg-secondary d-flex align-items-center justify-content-center text-white"
                  style={{ width: '50px', height: '50px' }}
                >
                  <i className="bi bi-person" style={{ fontSize: '1.1rem' }}></i>
                </div>
              )}
            </Col>
            <Col className="flex-grow-1">
              <h6 className="mb-1 fw-bold">{item.fullname}</h6>
              <p className="mb-0 text-muted">
                <i className="bi bi-telephone me-1"></i>
                {item.phone}
              </p>
            </Col>
            <Col xs="auto">
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => editStaff(item.id)}
                title="Edit Staff"
                className="me-2"
              >
                <i className="bi bi-pencil"></i>
              </Button>
              <Button
                variant="outline-danger"
                size="sm"
                onClick={() => removeStaff(item.id)}
                title="Remove Staff"
              >
                <i className="bi bi-trash"></i>
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>
    );
  };

  if (isLoading && dataSource.length === 0) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
        <div className="text-center">
          <Spinner animation="border" role="status" style={{ width: '3rem', height: '3rem' }}>
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <p className="mt-3 text-muted">Loading staff data...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <section className="contact-hero d-flex align-items-center">
        <div className="contact-hero-overlay"></div>
        <div className="container position-relative">
          <h1 className="display-5 fw-bold text-white mb-1">Staff <span className="text-warning">Management</span></h1>
          <p className="text-light mb-0">Add and manage your clinic staff from here.</p>
        </div>
      </section>

      <div className="container-md py-5">
        {/* Alert Messages */}
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert variant="success" dismissible onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        {/* Add/Edit Staff Form */}
        <Card className="mb-4 shadow-sm">
          <Card.Header className="bg-primary text-white">
            <h5 className="mb-0">
              {staffVal.id ? 'Edit Staff' : 'Add New Staff'}
            </h5>
          </Card.Header>
          <Card.Body>
            <Formik
              enableReinitialize={true}
              initialValues={staffVal}
              onSubmit={(values) => {
                handleSubmit(values);
              }}
              validationSchema={validationSchema}
            >
              {({ isSubmitting, values, setFieldValue }) => (
                <Form>
                  <Row>
                    <Col md={6}>
                      <BootstrapForm.Group className="mb-3">
                        <BootstrapForm.Label>
                          Staff Name <span className="text-danger">*</span>
                        </BootstrapForm.Label>
                        <Field
                          name="fullname"
                          type="text"
                          className="form-control"
                          placeholder="Enter staff name"
                        />
                        <ErrorMessage name="fullname" component="div" className="text-danger small mt-1" />
                      </BootstrapForm.Group>
                    </Col>
                    <Col md={6}>
                      <BootstrapForm.Group className="mb-3">
                        <BootstrapForm.Label>
                          Mobile No <span className="text-danger">*</span>
                        </BootstrapForm.Label>
                        <Field
                          name="phone"
                          type="text"
                          className="form-control"
                          placeholder="Enter mobile number"
                          maxLength="10"
                        />
                        <ErrorMessage name="phone" component="div" className="text-danger small mt-1" />
                      </BootstrapForm.Group>
                    </Col>
                  </Row>

                  <BootstrapForm.Group className="mb-3">
                    <BootstrapForm.Label>Permissions</BootstrapForm.Label>
                    <div>
                      {[
                        { key: 'can_next_skip', label: 'Update' },
                        { key: 'can_print_token', label: 'Print' },
                        { key: 'can_open_token', label: 'Open/Close' }
                      ].map((permission) => (
                        <BootstrapForm.Check
                          key={permission.key}
                          type="checkbox"
                          id={permission.key}
                          label={permission.label}
                          checked={values[permission.key]}
                          onChange={(e) => setFieldValue(permission.key, e.target.checked)}
                          className="me-3"
                        />
                      ))}
                    </div>
                  </BootstrapForm.Group>

                  <div className="d-flex justify-content-center">
                    <Button
                      type="submit"
                      variant="primary"
                      disabled={isSubmitting || isLoading}
                      className="px-4"
                    >
                      {isLoading ? (
                        <>
                          <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                          <span className="ms-2">Processing...</span>
                        </>
                      ) : (
                        <>
                          <i className="bi bi-person-plus me-2"></i>
                          {staffVal.id ? 'Update Staff' : 'Add Staff'}
                        </>
                      )}
                    </Button>
                  </div>
                </Form>
              )}
            </Formik>
          </Card.Body>
        </Card>

        {/* Staff List */}
        <Card className="shadow-sm">
          <Card.Header className="bg-secondary text-white">
            <h5 className="mb-0">Staff Members ({dataSource.length})</h5>
          </Card.Header>
          <Card.Body className="p-0">
            {dataSource.length === 0 ? (
              <div className="text-center py-4">
                <p className="text-muted mb-0">No staff members found</p>
              </div>
            ) : (
              <div className="p-3">
                {dataSource.map((item) => (
                  <StaffItem key={item.id} item={item} />
                ))}
              </div>
            )}
          </Card.Body>
        </Card>
      </div>
    </>
  );
};

export default StaffAll;
